import React, { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";
import Menu from "./Menu";
import { generatePath, Link } from "react-router-dom";
import ROUTES from "../../../route";
import Filters from "./Filters";
import Pagination from "../../assets/Pagination";
import Search from "./Search";

const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '') : '';

const Library = () => {
    const [books, setBooks] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [genres, setGenres] = useState([]);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [resetFilters, setResetFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('title');
    const [sortDir, setSortDir] = useState('asc');
    const [initialized, setInitialized] = useState(false);
    const booksPerPage = 10;

    useEffect(() => {
        (async () => {
            try {
                const response = await api.get('/library');
                setAuthors(response.data.authors);
                setGenres(response.data.genres);
            } catch (error) {
                console.error("Error fetching library metadata: ", error);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const params = new URLSearchParams({
                    authors: selectedAuthors.join(','),
                    genres: selectedGenres.join(','),
                    search: searchTerm,
                    page: currentPage,
                    per_page: booksPerPage,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                });
                const response = await api.get(`/filterLibrary?${params}`);
                setBooks(response.data.books);
                setLastPage(response.data.last_page);
            } catch (error) {
                console.error("Error fetching books: ", error);
            } finally {
                setInitialized(true);
            }
        })();
    }, [selectedAuthors, selectedGenres, searchTerm, currentPage, sortBy, sortDir]);

    useEffect(() => {
        if (resetFilters) setResetFilters(false);
    }, [resetFilters]);

    const pageNumbers = Array.from({ length: lastPage }, (_, i) => i + 1);

    const handleSearch = useCallback((event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    }, []);

    const handleAuthorChange = useCallback((authorId) => {
        setSelectedAuthors(prev =>
            prev.includes(authorId) ? prev.filter(id => id !== authorId) : [...prev, authorId]
        );
        setCurrentPage(1);
    }, []);

    const handleGenreChange = useCallback((genreId) => {
        setSelectedGenres(prev =>
            prev.includes(genreId) ? prev.filter(id => id !== genreId) : [...prev, genreId]
        );
        setCurrentPage(1);
    }, []);

    const handleClick = useCallback((event, pageNumber) => {
        event.preventDefault();
        setCurrentPage(pageNumber);
    }, []);

    const handleSort = useCallback((column) => {
        if (column === sortBy) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
        setCurrentPage(1);
    }, [sortBy]);

    const resetAllFilters = useCallback(() => {
        setSelectedGenres([]);
        setSelectedAuthors([]);
        setSearchTerm('');
        setResetFilters(true);
        setCurrentPage(1);
    }, []);

    const deleteBook = useCallback((id) => {
        api.delete(`/removeBook/${id}`)
            .then(() => setBooks(prev => prev.filter(book => book.id !== id)))
            .catch((error) => console.log('failed to delete book:', error.response?.data?.message || error.message));
    }, []);

    if (!initialized) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-center">
                    <span className="text-4xl animate-pulse">📚</span>
                    <p className="mt-4 text-warm-400 font-medium">Loading your library…</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="font-serif text-3xl font-bold text-warm-800">My Library</h1>
                <span className="text-sm text-warm-400">{books.length} book{books.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <Filters
                    genres={genres}
                    authors={authors}
                    filterBooksByGenre={handleGenreChange}
                    filterBooksByAuthor={handleAuthorChange}
                    resetAllFilters={resetAllFilters}
                    resetFilters={resetFilters}
                />
                <Search handleSearch={handleSearch} value={searchTerm} />
            </div>

            <div className="bg-white rounded-2xl border border-warm-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <Menu sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                        <tbody>
                            {books.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-warm-400 italic">
                                        No books found. Try adjusting your filters.
                                    </td>
                                </tr>
                            ) : books.map((book) => {
                                const rating = book.ratings?.[0]?.rating;
                                const synopsis = stripHtml(book.description || '');
                                const truncated = synopsis.length > 90 ? synopsis.slice(0, 90) + '…' : synopsis;

                                return (
                                    <tr key={book.id} className="border-b border-warm-100 hover:bg-warm-50 transition-colors">
                                        <td className="px-4 py-3 max-w-[180px]">
                                            <Link
                                                to={generatePath(ROUTES.BOOK_SHOW, { id: book.id })}
                                                className="font-medium text-warm-800 hover:text-warm-600 transition-colors block truncate"
                                                title={book.title}
                                            >
                                                {book.title}
                                            </Link>
                                        </td>

                                        <td className="px-4 py-3 max-w-[140px]">
                                            <span className="block truncate text-warm-600" title={book.author?.name}>
                                                {book.author?.name}
                                            </span>
                                        </td>

                                        <td className="px-4 py-3 max-w-[120px]">
                                            <div className="flex items-center gap-1">
                                                {book.genres[0] && (
                                                    <span className="bg-warm-100 text-warm-600 text-xs px-2 py-0.5 rounded-full truncate max-w-[80px]" title={book.genres[0].name}>
                                                        {book.genres[0].name}
                                                    </span>
                                                )}
                                                {book.genres.length > 1 && (
                                                    <span className="text-warm-400 text-xs whitespace-nowrap">+{book.genres.length - 1}</span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3 max-w-[220px]">
                                            <span className="block truncate text-warm-500 text-xs leading-relaxed" title={synopsis}>
                                                {truncated || <em className="text-warm-300">No description</em>}
                                            </span>
                                        </td>

                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {rating ? (
                                                <span className="text-amber-500 font-semibold text-sm">★ {rating}<span className="text-warm-300 font-normal">/10</span></span>
                                            ) : (
                                                <span className="text-warm-300 text-sm">—</span>
                                            )}
                                        </td>

                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 min-w-[90px]">
                                                <div className="flex-1 bg-warm-100 rounded-full h-1.5">
                                                    <div
                                                        className="bg-amber-400 h-1.5 rounded-full transition-all"
                                                        style={{ width: `${book.pivot?.progression ?? 0}%` }}
                                                    />
                                                </div>
                                                <span className="text-warm-500 text-xs whitespace-nowrap">
                                                    {book.pivot?.progression ?? 0}%
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => deleteBook(book.id)}
                                                className="text-warm-300 hover:text-red-400 transition-colors p-1 rounded"
                                                title="Remove from library"
                                            >
                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6l-1 14H6L5 6" />
                                                    <path d="M10 11v6M14 11v6" />
                                                    <path d="M9 6V4h6v2" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {lastPage > 1 && (
                    <div className="px-4 py-4 border-t border-warm-100">
                        <Pagination handleClick={handleClick} currentPage={currentPage} pageNumbers={pageNumbers} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Library;
